import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge, Flex, Pagination, Text } from "@mantine/core";
import useSWR from "swr";
import DataTable from "../../../../common/components/DataTable/DataTable";
import { useDebouncedSearchQuery } from "../../../../common/hooks/useDebouncedSearchQuery/useDebouncedSearchQuery";
import { fetcher } from "../../../../services/fetcher";
import {
  explorePlansApi,
  parseExplorerListResponse,
} from "../../services/explorePlansApi";
import {
  errorNotifications,
  successNotifications,
} from "../../../../utils/handleNotifications";
import ExplorePlanProfileModalForm from "./ExplorePlanProfileModalForm";
import { useSWRConfig } from "swr";
import { readPageParam } from "../../../../common/utils/listNavigation";

const LIST_SWR_CONFIG = Object.freeze({
  revalidateOnFocus: false,
  keepPreviousData: true,
});

const ExplorePlansList = () => {
  const { mutate: globalMutate } = useSWRConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const skipSearchPageReset = useRef(true);

  const [pageIndex, setPageIndex] = useState(() => readPageParam(searchParams));
  const [searchInputValue, setSearchInputValue] = useState("");
  const { apiSearchQuery: apiSearch, isSearchPending } =
    useDebouncedSearchQuery(searchInputValue);
  const [orderingValue, setOrderingValue] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const listUrl = useMemo(
    () => explorePlansApi.list({ page: pageIndex, search: apiSearch }),
    [pageIndex, apiSearch]
  );

  const swrKey = useMemo(() => {
    if (isSearchPending) return null;
    return listUrl;
  }, [isSearchPending, listUrl]);

  const { data, isLoading, isValidating } = useSWR(swrKey, fetcher, LIST_SWR_CONFIG);

  const { rows, total, pageSize } = useMemo(
    () => parseExplorerListResponse(data),
    [data]
  );

  const totalPages = Math.max(1, Math.ceil(total / (pageSize || 25)));

  const viewUrlForId = (id) =>
    `${window.location.origin}/cover-api/explore-plans/view/${id}`;

  const { data: editDetail } = useSWR(
    editRow?.id ? explorePlansApi.detail(editRow.id) : null,
    fetcher,
    { revalidateOnFocus: false }
  );

  const revalidateAllExplorerLists = useCallback(async () => {
    await globalMutate(
      (key) =>
        typeof key === "string" &&
        key.startsWith("cover-api/explorer-profiles") &&
        key.includes("?"),
      undefined,
      { revalidate: true }
    );
  }, [globalMutate]);

  const handlePublishToggle = useCallback(async (row) => {
    const action = row?.is_published ? "unpublish" : "publish";
    const endpoint =
      action === "publish"
        ? explorePlansApi.publish(row.id)
        : explorePlansApi.unpublish(row.id);

    try {
      await fetcher(endpoint, "POST", {});
      successNotifications(
        action === "publish"
          ? "Profile published successfully"
          : "Profile unpublished successfully"
      );
      await revalidateAllExplorerLists();
    } catch (e) {
      errorNotifications(e?.response?.data || "Action failed", e);
    }
  }, [revalidateAllExplorerLists]);

  const handleCreateSubmit = async (payload) => {
    setSavingProfile(true);
    try {
      await fetcher(explorePlansApi.create(), "POST", payload);
      successNotifications("Explorer profile created");
      setCreateOpen(false);
      setPageIndex(1);
      await revalidateAllExplorerLists();
    } catch (e) {
      errorNotifications(e?.response?.data || "Create failed", e);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleEditSubmit = async (payload) => {
    if (!editRow?.id) return;
    setSavingProfile(true);
    try {
      await fetcher(explorePlansApi.update(editRow.id), "PATCH", payload);
      successNotifications("Explorer profile updated");
      setEditRow(null);
      await revalidateAllExplorerLists();
    } catch (e) {
      errorNotifications(e?.response?.data || "Update failed", e);
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    setPageIndex(readPageParam(searchParams));
  }, [searchParams]);

  const handlePageChange = (page) => {
    setPageIndex(page);
    const next = new URLSearchParams(searchParams);
    if (page <= 1) next.delete("page");
    else next.set("page", String(page));
    setSearchParams(next, { replace: true });
  };

  const previousApiSearch = useRef(apiSearch);

  useEffect(() => {
    if (skipSearchPageReset.current) {
      skipSearchPageReset.current = false;
      previousApiSearch.current = apiSearch;
      return;
    }
    if (previousApiSearch.current === apiSearch) return;
    previousApiSearch.current = apiSearch;
    setPageIndex(1);
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    setSearchParams(next, { replace: true });
  }, [apiSearch, searchParams, setSearchParams]);

  const handleSearch = (event) => {
    setSearchInputValue(event.target.value);
  };

  const config = useMemo(
    () => ({
      fields: [
        { key: "cover_token", label: "Plan Code" },
        { key: "cover_title", label: "Plan Title" },
        { key: "region", label: "Region" },
        { key: "activity_type", label: "Activity" },
        {
          key: "difficulty",
          label: "Difficulty",
          render: (row) => row?.difficulty || "—",
        },
        {
          key: "is_published",
          label: "Published",
          render: (row) => (
            <Badge
              color={row?.is_published ? "green" : "yellow"}
              variant="filled"
              radius="xl"
            >
              {row?.is_published ? "Published" : "Draft"}
            </Badge>
          ),
        },
      ],
      selectionActions: [
        {
          label: "+ New Profile",
          handler: () => setCreateOpen(true),
        },
      ],
      actions: [
        {
          label: "Open",
          to: (row) => `open/${row.id}/itinerary`,
        },
      ],
      handler: [
        {
          label: "Edit",
          handler: (row) => setEditRow(row),
        },
        {
          label: (row) => (row?.is_published ? "Unpublish" : "Publish"),
          handler: (row) => handlePublishToggle(row),
        },
      ],
      onRowClick: (row) => {
        window.open(
          viewUrlForId(row.id),
          "_blank",
          "noopener,noreferrer"
        );
      },
    }),
    [handlePublishToggle]
  );

  return (
    <>
      <DataTable
        data={rows}
        config={config}
        loading={(!data && isLoading) || isSearchPending || isValidating}
        filterOptions={null}
        form={null}
        onSubmit={() => {}}
        onSearch={handleSearch}
        searchValue={searchInputValue}
        onOrdering={(v) => setOrderingValue(v)}
        orderingValue={orderingValue}
      />

      <Flex justify="space-between" align="center" mt="lg" px="sm">
        {!isLoading && (
          <Text size="sm" fw="bold" c="dimmed">
            Showing {rows.length} of {total} records
          </Text>
        )}
        <Pagination
          page={pageIndex}
          onChange={handlePageChange}
          total={totalPages}
          siblings={1}
        />
      </Flex>

      <ExplorePlanProfileModalForm
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        mode="create"
        initialData={{}}
        isSubmitting={savingProfile}
        onSubmit={handleCreateSubmit}
      />

      <ExplorePlanProfileModalForm
        opened={!!editRow}
        onClose={() => setEditRow(null)}
        mode="edit"
        initialData={editDetail || editRow || {}}
        isSubmitting={savingProfile}
        onSubmit={handleEditSubmit}
      />
    </>
  );
};

export default ExplorePlansList;
